"use client"
import Link from "next/link";
import {
  LayoutDashboard,
  Package,
  Wallet,
  User,
  LogOut,
  Shield,
  Home,
  Users,
  Menu,
  Truck,
  DollarSign,
  History,
  Share2,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { auth, db } from "@/lib/firebase";
import { onAuthStateChanged, User as FirebaseUser, signOut } from "firebase/auth";
import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import type { User as AppUser } from "@/lib/types";
import { toast } from "@/hooks/use-toast";

const navItems = [
    { title: "Dashboard", href: "/account", icon: LayoutDashboard },
    { title: "Meus Pedidos", href: "/account/orders", icon: Package },
    { title: "Wallet", href: "/account/wallet", icon: Wallet },
    { title: "Fidelidade", href: "/account/loyalty", icon: Star },
    { title: "Indicação", href: "/account/referrals", icon: Share2 },
    { title: "Minha Conta", href: "/account/settings", icon: User },
];

const homeNavItem = { title: "Voltar à Loja", href: "/", icon: Home };

const deliveryNavItems = [
    { title: "Entregas Atuais", href: "/account/delivery", icon: Truck },
    { title: "Meus Ganhos", href: "/account/delivery/earnings", icon: DollarSign },
    { title: "Histórico", href: "/account/delivery/history", icon: History },
];


const AccountSidebar = ({ user, appUser, onLogout }: { user: FirebaseUser, appUser: AppUser | null, onLogout: () => void }) => {
    
    const getNavItems = () => {
        if (appUser?.role === 'driver') {
            return deliveryNavItems;
        }

        let finalNavItems: {title: string, href: string, icon: any}[] = [...navItems];
        
        if (appUser?.role === 'employee' || appUser?.role === 'admin') {
            finalNavItems.push({ title: "Painel Equipe", href: "/admin/team", icon: Shield })
        }
        return finalNavItems;
    }

    return (
        <div className="flex h-full flex-col">
            <div className="flex-1">
                <Card className="border-none shadow-none bg-transparent">
                    <CardHeader className="flex flex-row items-center gap-4 p-4">
                        <Avatar>
                            <AvatarImage src={user.photoURL || `https://placehold.co/40x40.png?text=${user.displayName?.charAt(0)}`} />
                            <AvatarFallback>{user.displayName?.charAt(0) || 'U'}</AvatarFallback>
                        </Avatar>
                        <div>
                            <CardTitle className="text-base">{user.displayName || 'Usuário'}</CardTitle>
                            <CardDescription>{user.email}</CardDescription>
                        </div>
                    </CardHeader>
                </Card>
                <nav className="mt-4 grid gap-1 p-2">
                    {getNavItems().map((item) => (
                        <Link key={item.href} href={item.href}>
                            <Button variant="ghost" className="w-full justify-start gap-2">
                                <item.icon className="h-4 w-4" />
                                {item.title}
                            </Button>
                        </Link>
                    ))}
                     <Link href={homeNavItem.href}>
                        <Button variant="ghost" className="w-full justify-start gap-2 mt-4 border-t border-dashed pt-6">
                            <homeNavItem.icon className="h-4 w-4" />
                            {homeNavItem.title}
                        </Button>
                    </Link>
                </nav>
            </div>
            <div className="mt-auto p-2">
                <Button variant="outline" className="w-full justify-start gap-2" onClick={onLogout}>
                    <LogOut className="h-4 w-4" />
                    Sair
                </Button>
            </div>
        </div>
    );
};


export default function AccountLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const authUnsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userUnsubscribe = onSnapshot(userDocRef, (userDoc) => {
          if (userDoc.exists()) {
              const userData = userDoc.data() as AppUser;
              if (userData.isBlocked) {
                  signOut(auth).then(() => {
                      toast({
                          variant: 'destructive',
                          title: 'Acesso Bloqueado',
                          description: 'Sua conta foi bloqueada. Contate o suporte.'
                      });
                      router.replace('/404');
                  });
              } else {
                  setAppUser(userData);
              }
          } else {
              // User authenticated but no data in Firestore.
              // This can happen briefly during signup.
              // Do not log out immediately to avoid race conditions.
              // The app should handle the `appUser` state being null.
              console.warn("User document not found for authenticated user:", currentUser.uid);
          }
        });
        return () => userUnsubscribe();
      } else {
        router.push('/login');
      }
    });
    return () => authUnsubscribe();
  }, [router]);

  const handleLogout = async () => {
    await auth.signOut();
    router.push('/login');
  };
  
  if (!user) {
    return <div className="flex h-screen items-center justify-center">Carregando...</div>;
  }

  return (
       <div className="flex min-h-screen w-full flex-col">
          <header className="sticky top-0 flex h-14 items-center justify-between gap-4 border-b bg-background px-4 sm:hidden">
              <h1 className="text-lg font-semibold">Minha Conta</h1>
              <Sheet>
                <SheetTrigger asChild>
                    <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    >
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">Toggle navigation menu</span>
                    </Button>
                </SheetTrigger>
                <SheetContent side="left" className="flex flex-col p-0">
                    <SheetHeader className="p-4 border-b">
                        <SheetTitle className="text-left">Menu</SheetTitle>
                    </SheetHeader>
                    <AccountSidebar user={user} appUser={appUser} onLogout={handleLogout} />
                </SheetContent>
              </Sheet>
          </header>
          <div className="flex flex-1">
            <aside className="hidden w-72 flex-col border-r bg-card p-4 sm:flex">
              <AccountSidebar user={user} appUser={appUser} onLogout={handleLogout} />
            </aside>
            <main className="flex-1 overflow-auto p-4 sm:p-8 bg-muted/20">
                {children}
            </main>
          </div>
       </div>
  );
}
